import {
    IonBackButton,
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonSpinner,
    IonTitle,
    IonToolbar
} from "@ionic/react";
import {
    alertCircleOutline,
    calendarOutline,
    chevronDownOutline,
    chevronUpOutline,
    refreshOutline
} from "ionicons/icons";

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { useParams } from "react-router-dom";
import {
    calculateFantasyGame,
    FantasyGameCalculation,
    FantasyRosterPlayer,
    FantasySettings,
    RealMatch
} from "../services/fantasyGameCalculator";
import Cookies from "js-cookie";
import "./FantasyGame.css";
import FantasyMatchModal from "../components/FantasyMatchModal";


interface RouteParams {
    id: string;
}

interface FantasyTeam {
    id: string;
    name: string;
    manager: string;
    roster: FantasyRosterPlayer[];
}
interface TeamProjectionSnapshot {
    totalProjectedPoints: number;
    starterPlayerIds: string[];
    players: Record<string, number>;
}

interface ProjectionSnapshot {
    capturedAt?: string;
    updatedAt?: string;

    myTeam: TeamProjectionSnapshot | null;
    opponentTeam: TeamProjectionSnapshot | null;
}


interface FantasyGameState {
    success: boolean;
    updatedAt: string;
    projectionSnapshot?: ProjectionSnapshot | null;
    projectionForecast?: ProjectionSnapshot | null;

    league: {
        id: string;
        name: string;
        competitionId: string;
        competitionName: string;
    };

    week: {
        realRound: number;
        fantasyRound: number;
        status: string;
    };

    fantasyMatch: {
        id: string;
        isBye: boolean;
        myTeam: FantasyTeam;
        opponentTeam: FantasyTeam;
    };

    settings: FantasySettings;
    realMatches: RealMatch[];
}

function getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
        scheduled: "Programado",
        live: "En vivo",
        final: "Final",
        postponed: "Pospuesto",
        today: "Hoy"
    };

    return labels[status] || "Pendiente";
}

function getStatusClass(status: string): string {
    const classes: Record<string, string> = {
        scheduled: "fantasy-status--scheduled",
        live: "fantasy-status--live",
        final: "fantasy-status--final",
        postponed: "fantasy-status--postponed",
        today: "fantasy-status--today"
    };

    return classes[status] || "fantasy-status--scheduled";
}

function formatDate(date?: string): string {
    if (!date) {
        return "Fecha pendiente";
    }

    const parsedDate = new Date(`${date}T12:00:00`);

    if (Number.isNaN(parsedDate.getTime())) {
        return date;
    }

    return new Intl.DateTimeFormat(
        "es-MX",
        {
            day: "numeric",
            month: "short",
            year: "numeric"
        }
    ).format(parsedDate);
}
function getStarterSlotOrder(slot: string): number {
    const normalizedSlot = slot
        .trim()
        .toUpperCase();

    const slotOrder: Record<string, number> = {
        QB: 1,
        RB: 2,
        WR: 3,
        FLEX: 4,
        K: 5,
        DEF: 6,

        /* Variantes compatibles para defensiva */
        DST: 6,
        "D/ST": 6
    };

    return slotOrder[normalizedSlot] ?? 99;
}

function sortStarters(
    starters: FantasyGameCalculation["myTeam"]["starters"]
): FantasyGameCalculation["myTeam"]["starters"] {
    return [...starters].sort(function (left, right) {
        const orderDifference =
            getStarterSlotOrder(left.slot) -
            getStarterSlotOrder(right.slot);

        if (orderDifference !== 0) {
            return orderDifference;
        }

        return left.name.localeCompare(
            right.name,
            "es-MX"
        );
    });
}

function formatPoints(points: number): string {
    return points.toFixed(2);
}
interface DisplayPlayer {
    id: string;
    name: string;
    teamName: string;
    position: string;
    slot: string;
    points: number;
    statLine: string;
    initialProjection: number | null;
}


function PlayerRow(props: {
    player: DisplayPlayer;
}): JSX.Element {
    const { player } = props;

    return (
        <article className="fantasy-player-row">
            <div className="fantasy-player-row__slot">
                {player.slot}
            </div>

            <div className="fantasy-player-row__main">
                <strong>{player.name}</strong>

                <span>
                    {player.position} · {player.teamName}
                </span>

                <small>{player.statLine}</small>
            </div>

            <div className="fantasy-player-row__projection">
                <strong>
                    {player.initialProjection === null
                        ? "—"
                        : formatPoints(
                            player.initialProjection
                        )}
                </strong>

                <small>PROY.</small>
            </div>

            <div className="fantasy-player-row__points">
                <strong>
                    {formatPoints(player.points)}
                </strong>

                <small>REAL</small>
            </div>
        </article>
    );
}


function RosterCard(props: {
    title: string;
    teamName: string;
    manager: string;
    totalPoints: number;
    totalProjection: number | null;
    starters: FantasyGameCalculation["myTeam"]["starters"];
    bench: FantasyGameCalculation["myTeam"]["bench"];
    playerProjections: Record<string, number>;
    projectionLabel: string;
    isMyTeam?: boolean;
}): JSX.Element {
    const {
        title,
        teamName,
        manager,
        totalPoints,
        totalProjection,
        starters,
        bench,
        playerProjections,
        projectionLabel,
        isMyTeam
    } = props;

    const sortedStarters = sortStarters(starters);

    function toDisplayPlayer(
        player: FantasyGameCalculation["myTeam"]["starters"][number]
    ): DisplayPlayer {
        return {
            ...player,
            initialProjection:
                playerProjections[player.id] ?? null
        };
    }

    return (
        <section className="fantasy-roster-card">
            <header className="fantasy-roster-card__header">
                <div>
                    <span className="fantasy-roster-card__eyebrow">
                        {title}
                    </span>

                    <h2>{teamName}</h2>

                    {manager ? (
                        <p>{manager}</p>
                    ) : null}
                </div>

                <div className="fantasy-roster-card__totals">
                    <div>
                        <strong>
                            {totalProjection === null
                                ? "—"
                                : formatPoints(
                                    totalProjection
                                )}
                        </strong>

                        <span>{projectionLabel}</span>

                    </div>

                    <div className="fantasy-roster-card__real-total">
                        <strong>
                            {formatPoints(totalPoints)}
                        </strong>

                        <span>
                            {isMyTeam
                                ? "TUS PUNTOS"
                                : "PUNTOS REALES"}
                        </span>
                    </div>
                </div>
            </header>

            <div className="fantasy-roster-card__column-headings">
                <span />
                <span />
                <span>PROY.</span>
                <span>REAL</span>
            </div>

            <div className="fantasy-roster-card__section">
                <h3>Titulares</h3>

                {sortedStarters.length > 0 ? (
                    sortedStarters.map(function (player) {
                        return (
                            <PlayerRow
                                key={player.id}
                                player={toDisplayPlayer(player)}
                            />
                        );
                    })
                ) : (
                    <p className="fantasy-empty">
                        No hay titulares registrados.
                    </p>
                )}
            </div>

            <div className="fantasy-roster-card__section fantasy-roster-card__section--bench">
                <h3>Banca</h3>

                {bench.length > 0 ? (
                    bench.map(function (player) {
                        return (
                            <PlayerRow
                                key={player.id}
                                player={toDisplayPlayer(player)}
                            />
                        );
                    })
                ) : (
                    <p className="fantasy-empty">
                        No hay jugadores en banca.
                    </p>
                )}
            </div>
        </section>
    );
}


const FantasyGame: React.FC = function () {
    const { id } = useParams<RouteParams>();

    const [data, setData] =
        useState<FantasyGameState | null>(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [
    selectedRealMatch,
    setSelectedRealMatch
] = useState<RealMatch | null>(null);
    const [isRealScheduleOpen, setIsRealScheduleOpen] =
    useState(false);

const loadFantasyGame = useCallback(
    async function (
        showLoader = false
    ): Promise<void> {
        if (!id) {
            setError("No se especificó la liga fantasy.");
            setLoading(false);
            return;
        }

        if (showLoader) {
            setRefreshing(true);
        }

        try {
            const accessToken =
                Cookies.get("access_token") || "";

            const accessUsername =
                Cookies.get("access_username") || "";

                console.log("Autenticación enviada:", {
                    username: accessUsername,
                    hasToken: Boolean(accessToken)
                });

            const response = await fetch(
                "https://fantasy.ftblscore.com/create_league/" +
                    `control_FantasyGameState.php?id=${encodeURIComponent(id)}`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        Accept: "application/json",
                        "X-Access-Token": accessToken,
                        "X-Access-Username": accessUsername
                    }
                }
            );

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                        "No fue posible cargar el enfrentamiento."
                );
            }

            setData(result);
            setError("");
        } catch (requestError) {
            setError(
                requestError instanceof Error
                    ? requestError.message
                    : "Ocurrió un error al cargar la información."
            );
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    },
    [id]
);


    useEffect(
        function () {
            loadFantasyGame();
        },
        [loadFantasyGame]
    );

    /*
        La frecuencia cambia según el estado de la jornada:
        live      → 30 segundos
        today     → 60 segundos
        scheduled → 5 minutos
        final     → no consulta automáticamente
    */
    useEffect(
        function () {
            if (!data) {
                return;
            }

            const pollingTimes: Record<string, number> = {
                live: 30000,
                today: 60000,
                scheduled: 300000
            };

            const refreshTime =
                pollingTimes[data.week.status];

            if (!refreshTime) {
                return;
            }

            const intervalId = window.setInterval(
                function () {
                    loadFantasyGame();
                },
                refreshTime
            );

            return function () {
                window.clearInterval(intervalId);
            };
        },
        [
            data,
            loadFantasyGame
        ]
    );

    const calculation = useMemo(
        function (): FantasyGameCalculation | null {
            if (!data) {
                return null;
            }

            return calculateFantasyGame(
                data.settings,
                data.fantasyMatch.myTeam.roster || [],
                data.fantasyMatch.opponentTeam.roster || [],
                data.realMatches || []
            );
        },
        [data]
    );


    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonBackButton
                                defaultHref={`/fantasyLeague/${id}`}
                            />
                        </IonButtons>

                        <IonTitle>Fantasy</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent>
                    <div className="fantasy-loading">
                        <IonSpinner name="crescent" />
                        <p>Cargando enfrentamiento...</p>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    if (error || !data || !calculation) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonBackButton
                                defaultHref={`/fantasyLeague/${id}`}
                            />

                        </IonButtons>

                        <IonTitle>Fantasy</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent>
                    <main className="fantasy-error">
                        <IonIcon icon={alertCircleOutline} />

                        <h1>No fue posible cargar la jornada</h1>

                        <p>
                            {error ||
                                "La información no está disponible."}
                        </p>

                        <IonButton
                            onClick={function () {
                                loadFantasyGame(true);
                            }}
                        >
                            Intentar nuevamente
                        </IonButton>
                    </main>
                </IonContent>
            </IonPage>
        );
    }

    const myPoints = calculation.myTeam.totalPoints;
    const opponentPoints =
        calculation.opponentTeam.totalPoints;

        /*
    Antes de iniciar la jornada, la proyección debe cambiar cuando
    el usuario modifica sus titulares. Por eso se usa el forecast.

    El snapshot sólo tiene prioridad durante una jornada en vivo
    o finalizada, porque representa la alineación ya congelada.
*/
const shouldUseFrozenSnapshot =
    data.week.status === "live" ||
    data.week.status === "final";

const projectionSource =
    shouldUseFrozenSnapshot &&
    data.projectionSnapshot
        ? data.projectionSnapshot
        : data.projectionForecast ??
            data.projectionSnapshot ??
            null;

const projectionLabel =
    shouldUseFrozenSnapshot &&
    data.projectionSnapshot
        ? "PROY. INICIAL"
        : data.projectionForecast
            ? "PROY. ACTUAL"
            : data.projectionSnapshot
                ? "PROY. INICIAL"
                : "PROYECCIÓN";

const myProjectionData =
    projectionSource?.myTeam ?? null;

const opponentProjectionData =
    projectionSource?.opponentTeam ?? null;

const myInitialProjection =
    myProjectionData?.totalProjectedPoints ?? null;

const opponentInitialProjection =
    opponentProjectionData?.totalProjectedPoints ?? null;

const myPlayerProjections =
    myProjectionData?.players ?? {};

const opponentPlayerProjections =
    opponentProjectionData?.players ?? {};

    const isBye = data.fantasyMatch.isBye === true;
    const liveMatchesCount = data.realMatches.filter(
    function (match) {
        return match.status === "live";
    }
).length;



    const isWinning = !isBye && myPoints > opponentPoints;
    const isLosing = !isBye && myPoints < opponentPoints;
    const isTied = !isBye && myPoints === opponentPoints;


    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton
                            defaultHref={`/fantasyLeague/${id}`}
                        />
                    </IonButtons>

                    <IonTitle>
                        {data.league.name}
                    </IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar enfrentamiento"
                            disabled={refreshing}
                            onClick={function () {
                                loadFantasyGame(true);
                            }}
                        >
                            {refreshing ? (
                                <IonSpinner name="crescent" />
                            ) : (
                                <IonIcon icon={refreshOutline} />
                            )}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen>
                <main className="fantasy-game">
                    <section className="fantasy-game__hero">
                        <span className="fantasy-game__competition">
                            {data.league.competitionName}
                        </span>

                        <h1>
                            Jornada {data.week.realRound}
                        </h1>

                        <div className="fantasy-game__status-row">
                            <IonBadge
                                className={getStatusClass(
                                    data.week.status
                                )}
                            >
                                {getStatusLabel(
                                    data.week.status
                                )}
                            </IonBadge>

                            <span>
                                Actualizado{" "}
                                {new Intl.DateTimeFormat(
                                    "es-MX",
                                    {
                                        hour: "2-digit",
                                        minute: "2-digit"
                                    }
                                ).format(
                                    new Date(data.updatedAt)
                                )}
                            </span>
                        </div>
                    </section>

                    <section className="fantasy-scoreboard">
                        <article className="fantasy-scoreboard__team">
                            <span>
                                {data.fantasyMatch.myTeam.name}
                            </span>
<div className="fantasy-scoreboard__score-group">
    <strong
        className={
            isWinning
                ? "fantasy-scoreboard__score fantasy-scoreboard__score--winning"
                : "fantasy-scoreboard__score"
        }
    >
        {formatPoints(myPoints)}
    </strong>

    <span className="fantasy-scoreboard__projection">
        {projectionLabel}:{" "}
        {myInitialProjection === null
            ? "—"
            : formatPoints(myInitialProjection)}
    </span>
</div>

<small className="fantasy-scoreboard__manager">
    {data.fantasyMatch.myTeam.manager}
</small>

                        </article>

                        <div className="fantasy-scoreboard__versus">
                            <span>
                                {isBye
                                    ? "Semana bye"
                                    : isTied
                                        ? "Empate"
                                        : isWinning
                                            ? "Vas ganando"
                                            : isLosing
                                                ? "Vas perdiendo"
                                                : "VS"}
                            </span>

                            <strong>
                                {isBye ? "—" : "VS"}
                            </strong>
                        </div>


                        <article className="fantasy-scoreboard__team fantasy-scoreboard__team--opponent">
                            <span>
                                {isBye
                                    ? "Sin rival"
                                    : data.fantasyMatch.opponentTeam.name}
                            </span>

<div className="fantasy-scoreboard__score-group">
    <strong
        className={
            isLosing
                ? "fantasy-scoreboard__score fantasy-scoreboard__score--winning"
                : "fantasy-scoreboard__score"
        }
    >
        {isBye
            ? "—"
            : formatPoints(opponentPoints)}
    </strong>

    <span className="fantasy-scoreboard__projection">
        {isBye
            ? "Sin rival"
            : `${projectionLabel}: ${
                opponentInitialProjection === null
                    ? "—"
                    : formatPoints(opponentInitialProjection)
            }`}
    </span>
</div>

<small className="fantasy-scoreboard__manager">
    {isBye
        ? "No tienes rival esta jornada"
        : data.fantasyMatch.opponentTeam.manager}
</small>


                        </article>
                    </section>
<section className="fantasy-real-schedule">
    <button
        type="button"
        className={[
            "fantasy-real-schedule__toggle",
            isRealScheduleOpen
                ? "fantasy-real-schedule__toggle--open"
                : ""
        ].join(" ")}
        aria-expanded={isRealScheduleOpen}
        aria-controls="fantasy-real-schedule-list"
        onClick={function () {
            setIsRealScheduleOpen(function (currentValue) {
                return !currentValue;
            });
        }}
    >
        <span className="fantasy-real-schedule__icon">
            <IonIcon icon={calendarOutline} />
        </span>

        <span className="fantasy-real-schedule__copy">
            <small>PARTIDOS</small>

            <strong>
                {liveMatchesCount > 0
                    ? `${liveMatchesCount} partido${
                        liveMatchesCount === 1 ? "" : "s"
                    } en vivo`
                    : `Ver calendario · ${data.realMatches.length} partidos`}
            </strong>
        </span>

        <span className="fantasy-real-schedule__action">
            {isRealScheduleOpen ? "Cerrar" : "Abrir"}

            <IonIcon
                icon={
                    isRealScheduleOpen
                        ? chevronUpOutline
                        : chevronDownOutline
                }
            />
        </span>
    </button>

    {isRealScheduleOpen && (
        <div
            className="fantasy-real-schedule__content"
            id="fantasy-real-schedule-list"
        >
            <div className="fantasy-real-matches">
                {data.realMatches.map(function (match) {
                    return (
                        <article
                            className="fantasy-real-match fantasy-real-match--selectable"
                            key={match.id}
                            role="button"
                            tabIndex={0}
                            aria-label={`Abrir partido entre ${match.local.name} y ${match.visitor.name}`}
                            onClick={function () {
                                setSelectedRealMatch(match);
                            }}
                            onKeyDown={function (event) {
                                if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                ) {
                                    event.preventDefault();
                                    setSelectedRealMatch(match);
                                }
                            }}
                        >
                            <div className="fantasy-real-match__meta">
                                <span>
                                    {formatDate(match.date)}
                                    {match.hour
                                        ? ` · ${match.hour}`
                                        : ""}
                                </span>

                                <IonBadge
                                    className={getStatusClass(
                                        match.status
                                    )}
                                >
                                    {getStatusLabel(match.status)}
                                </IonBadge>
                            </div>

                            <div className="fantasy-real-match__score">
                                <strong>{match.local.name}</strong>

                                <span>
                                    {match.local.score || "—"}
                                </span>

                                <b>-</b>

                                <span>
                                    {match.visitor.score || "—"}
                                </span>

                                <strong>{match.visitor.name}</strong>
                            </div>
                        </article>
                    );
                })}
            </div>
        </div>
    )}
</section>
                    <section className="fantasy-section">
                        <div className="fantasy-section__title">
                            <div>
                                <span>Comparativa</span>
                                <h2>Alineaciones</h2>
                            </div>

                            <p>
                                Sólo titulares suman al marcador.
                            </p>
                        </div>

                        <div className="fantasy-rosters">
                            <RosterCard
    title="Mi equipo"
    teamName={data.fantasyMatch.myTeam.name}
    manager={data.fantasyMatch.myTeam.manager}
    totalPoints={myPoints}
    totalProjection={myInitialProjection}
    starters={calculation.myTeam.starters}
    bench={calculation.myTeam.bench}
    playerProjections={myPlayerProjections}
    projectionLabel={projectionLabel}
    isMyTeam
/>


                            <RosterCard
    title={isBye ? "Semana bye" : "Rival"}
    teamName={
        isBye
            ? "Sin rival asignado"
            : data.fantasyMatch.opponentTeam.name
    }
    manager={
        isBye
            ? "No tienes enfrentamiento esta jornada."
            : data.fantasyMatch.opponentTeam.manager
    }
    totalPoints={opponentPoints}
    totalProjection={
        isBye
            ? null
            : opponentInitialProjection
    }
    starters={calculation.opponentTeam.starters}
    bench={calculation.opponentTeam.bench}
    projectionLabel={projectionLabel}
    playerProjections={
        isBye
            ? {}
            : opponentPlayerProjections
    }
/>


                        </div>
                    </section>

                </main>
            </IonContent>
            <FantasyMatchModal
    isOpen={selectedRealMatch !== null}
    match={selectedRealMatch}
    fantasySettings={data.settings}
    onDismiss={function () {
        setSelectedRealMatch(null);
    }}
/>

        </IonPage>
    );
};

export default FantasyGame;
